import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { useNavigate } from "react-router-dom";
import type { MultiAnnotatedRecord, ReviewCounts, ReviewDetailView, ReviewQueueItem, ReviewSeedRecord, TriageStatus } from "@shared/types";
import {
  fetchReviewQueue,
  fetchReviewCounts,
  fetchReviewDetail,
  fetchReviewDraft,
  fetchReviewSeeds,
  resolveReview,
  fetchAgreement,
  fetchMultiAnnotated,
  setCanonical,
  rewriteDraft,
  type ReviewQueueFilter,
  type AgreementResponse
} from "../lib/reviewApi";
import { prefillFromDraft, DRAFT_VERDICT_LABEL, type ReviewDraft } from "./draft";
import { getUser } from "../lib/auth";
import { VERDICT_ACTIONS, type VerdictAction } from "./verdicts";
import { aiAssistMode, diffTokens, type DiffOp } from "./ai-assist";

/** Reason chips a reviewer can attach to a correction (saved on the gold seed). */
const REASON_OPTIONS = ["漏关键点", "事实错", "表述不清", "该拒答"] as const;

/** Marks a rating inferred from the user's next message (noisier than a click). */
const IMPLICIT_BADGE = (
  <span
    className="rv-badge implicit"
    data-tip="隐式信号：由用户的后续消息推断得出（非主动点踩），可能有误判，请酌情参考。"
  >
    隐式·推断
  </span>
);

/**
 * Render one column of the polish preview: the left side shows shared + deleted
 * text (deletions struck through), the right side shows shared + inserted text.
 */
function renderDiffSide(ops: DiffOp[], side: "left" | "right"): JSX.Element[] {
  const out: JSX.Element[] = [];
  ops.forEach((op, i) => {
    if (op.type === "equal") out.push(<span key={i}>{op.text}</span>);
    else if (op.type === "del" && side === "left") out.push(<del key={i} className="rv-del">{op.text}</del>);
    else if (op.type === "ins" && side === "right") out.push(<ins key={i} className="rv-ins">{op.text}</ins>);
  });
  return out;
}

/**
 * Strip a leading annotator label like 【agent2的修正】 that some seeded
 * annotations carry — it's noise for reviewers, not part of the answer.
 */
function stripAnnotatorLabel(gold: string | null): string | null {
  if (gold === null) return null;
  return gold.replace(/^【[^】]*的修正】\s*/, "");
}

/** Normalize a multi-annotated record: drop label prefixes from every gold field. */
function cleanMulti(rec: MultiAnnotatedRecord): MultiAnnotatedRecord {
  return {
    ...rec,
    canonicalGold: stripAnnotatorLabel(rec.canonicalGold),
    annotations: rec.annotations.map((a) => ({ ...a, gold: stripAnnotatorLabel(a.gold) }))
  };
}
const STATUS_LABELS: Record<TriageStatus, string> = {
  new: "待标注",
  in_progress: "标注中",
  resolved: "已处理",
  wont_fix: "无需修正"
};

/** One clickable filter chip in the unified bar. `admin` chips only render for admins. */
const CHIPS: { key: string; label: string; admin?: boolean }[] = [
  { key: "new", label: "待标注" },
  { key: "resolved", label: "已处理" },
  { key: "wont_fix", label: "无需修正" },
  { key: "all", label: "全部" },
  { key: "down", label: "点踩" },
  { key: "handoff", label: "转人工" },
  { key: "batch", label: "批量判错" },
  { key: "multi", label: "多标注", admin: true },
  { key: "disagreement", label: "分歧", admin: true },
  { key: "seeds", label: "已存种子" }
];

function countFor(c: ReviewCounts | null, key: string): number | undefined {
  if (c === null) return undefined;
  switch (key) {
    case "new":
      return c.pending;
    case "resolved":
      return c.resolved;
    case "wont_fix":
      return c.wontFix;
    case "all":
      return c.all;
    case "down":
      return c.down;
    case "handoff":
      return c.handoff;
    case "batch":
      return c.batch;
    case "multi":
      return c.multi;
    case "disagreement":
      return c.disagreement;
    case "seeds":
      return c.seeds;
    default:
      return undefined;
  }
}

function filterFor(key: string): ReviewQueueFilter {
  switch (key) {
    case "new":
      return { status: "new" };
    case "resolved":
      return { status: "resolved" };
    case "wont_fix":
      return { status: "wont_fix" };
    case "down":
      return { signal: "down" };
    case "handoff":
      return { signal: "handoff" };
    case "batch":
      return { signal: "batch" };
    case "multi":
      return { multi: "1" };
    case "disagreement":
      return { disagreement: "1" };
    default:
      return {}; // "all"
  }
}

/**
 * /review annotation console. A single flat filter bar (each chip a clickable
 * view with a count), a queue list, and a detail panel where a reviewer corrects
 * the answer. For multi-annotated records the admin sees every annotation inline
 * as reference and picks or edits the final answer. Reviewer-gated (admin ∪ agent).
 */
export function ReviewConsole(): JSX.Element {
  const navigate = useNavigate();
  const [active, setActive] = useState<string>("new");
  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  const [seeds, setSeeds] = useState<ReviewSeedRecord[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<ReviewDetailView | null>(null);
  const [after, setAfter] = useState("");
  const [reasons, setReasons] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<ReviewDraft | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  // Stale-guard: track which annotation id triggered the current in-flight draft fetch.
  const openedIdRef = useRef<number | null>(null);

  const isAdmin = getUser()?.role === "admin";
  const [agreement, setAgreement] = useState<AgreementResponse | null>(null);
  const [multi, setMulti] = useState<MultiAnnotatedRecord[] | null>(null);
  const [counts, setCounts] = useState<ReviewCounts | null>(null);
  const [rewriting, setRewriting] = useState(false);
  // AI assist state. `editingAfter` = reviewer opted into typing from the empty
  // guide; `polishPreview` holds the pending non-destructive polish result;
  // `polishUndo` keeps the pre-polish text for a one-shot undo after 采纳.
  const [editingAfter, setEditingAfter] = useState(false);
  const [polishPreview, setPolishPreview] = useState<{ original: string; rewritten: string } | null>(null);
  const [polishUndo, setPolishUndo] = useState<string | null>(null);
  // The user's attached screenshot, opened full-size in a lightbox (data URL).
  const [lightbox, setLightbox] = useState<string | null>(null);

  const isSeeds = active === "seeds";

  const loadCounts = useCallback(async () => {
    try {
      setCounts(await fetchReviewCounts());
    } catch {
      setCounts(null);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      setError(null);
      setItems(await fetchReviewQueue(filterFor(active)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [active]);

  const loadSeeds = useCallback(async () => {
    try {
      setError(null);
      setSeeds(await fetchReviewSeeds());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const loadAdmin = useCallback(() => {
    fetchAgreement().then(setAgreement).catch(() => setAgreement(null));
    fetchMultiAnnotated().then((recs) => setMulti(recs.map(cleanMulti))).catch(() => setMulti(null));
  }, []);

  useEffect(() => {
    void loadCounts();
    if (isSeeds) {
      void loadSeeds();
    } else {
      void loadQueue();
      if (isAdmin) loadAdmin();
    }
  }, [active, isSeeds, loadQueue, loadSeeds, loadCounts, loadAdmin, isAdmin]);

  const openItem = useCallback(async (id: number) => {
    setSelected(id);
    setDetail(null);
    setDraft(null);
    setDraftLoading(false);
    setEditingAfter(false);
    setPolishPreview(null);
    setPolishUndo(null);
    openedIdRef.current = id;
    try {
      const d = await fetchReviewDetail(id);
      setDetail(d);
      setAfter(d.gold?.after ?? "");
      setReasons(d.gold?.reasons ?? []);
      // Fire draft fetch non-blocking. Only set draftLoading after detail succeeds
      // so a failed detail fetch never leaves the banner stuck.
      setDraftLoading(true);
      const idAtRequest = id;
      fetchReviewDraft(id)
        .then((dr) => {
          if (openedIdRef.current !== idAtRequest) return; // stale — user moved on
          setDraft(dr);
          const pre = prefillFromDraft(d.gold?.after ?? null, dr);
          if (pre !== null) setAfter(pre);
        })
        .catch(() => {
          if (openedIdRef.current === idAtRequest) setDraft(null);
        })
        .finally(() => {
          if (openedIdRef.current === idAtRequest) setDraftLoading(false);
        });
    } catch (err) {
      setDraftLoading(false);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const toggleReason = (r: string): void =>
    setReasons((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const act = useCallback(
    async (action: VerdictAction["action"]) => {
      if (selected === null) return;
      setBusy(true);
      try {
        await resolveReview(selected, action === "edit" ? { action, after, reasons } : { action });
        setSelected(null);
        setDetail(null);
        await loadQueue();
        void loadCounts();
        if (isAdmin) loadAdmin();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [selected, after, reasons, loadQueue, loadCounts, loadAdmin, isAdmin]
  );

  // Empty box → draft a standard answer from the model's original. An empty box
  // has nothing to lose, so the draft fills it directly (no preview).
  const doDraft = useCallback(async () => {
    if (selected === null) return;
    const source = detail?.before ?? "";
    if (source.trim().length === 0) return;
    setRewriting(true);
    try {
      const r = await rewriteDraft(selected, source);
      if (r !== null) {
        setAfter(r);
        setEditingAfter(true);
      }
    } catch {
      /* best-effort — leave the box empty on failure */
    } finally {
      setRewriting(false);
    }
  }, [selected, detail]);

  // Non-empty box → polish the reviewer's text. Non-destructive: stash the result
  // in a preview the reviewer adopts or discards; never overwrite in place.
  const doPolish = useCallback(async () => {
    if (selected === null) return;
    const source = after;
    if (source.trim().length === 0) return;
    setRewriting(true);
    try {
      const r = await rewriteDraft(selected, source);
      // Skip the preview when the model returns nothing or the same text.
      if (r !== null && r.trim() !== source.trim()) setPolishPreview({ original: source, rewritten: r });
    } catch {
      /* best-effort — leave the draft as-is on failure */
    } finally {
      setRewriting(false);
    }
  }, [selected, after]);

  const adoptPolish = useCallback(() => {
    if (polishPreview === null) return;
    setPolishUndo(polishPreview.original);
    setAfter(polishPreview.rewritten);
    setPolishPreview(null);
  }, [polishPreview]);

  const undoPolish = useCallback(() => {
    setPolishUndo((prev) => {
      if (prev !== null) setAfter(prev);
      return null;
    });
  }, []);

  const saveCanonical = useCallback(
    async (responseId: string, gold: string) => {
      if (gold.trim().length === 0) return;
      setBusy(true);
      try {
        await setCanonical(responseId, gold);
        setSelected(null);
        setDetail(null);
        await loadQueue();
        void loadCounts();
        loadAdmin();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [loadQueue, loadCounts, loadAdmin]
  );

  function signalBadge(signal: string, reason?: string): JSX.Element {
    const label =
      signal === "handoff" ? "转人工" : signal === "batch" ? "批量判错" : `点踩${reason ? ` · ${reason}` : ""}`;
    return <span className={`rv-badge ${signal}`}>{label}</span>;
  }

  // The multi-annotation record (if any) for the currently open detail — shown
  // inline in the detail panel as reference when an admin picks the final answer.
  const detailResponseId = detail?.responseId ?? null;
  const currentMulti =
    isAdmin && detailResponseId !== null ? multi?.find((r) => r.responseId === detailResponseId) ?? null : null;

  // AI assist mode for the correction box: draft (empty + model answer), polish
  // (has text), or disabled. Drives which entry the panel shows.
  const aiMode = aiAssistMode(after, detail?.before ?? "");
  // Show the empty-state guide until the reviewer starts typing or adopts text.
  const showAfterGuide = after.trim().length === 0 && !editingAfter && polishPreview === null;
  const diffOps = polishPreview !== null ? diffTokens(polishPreview.original, polishPreview.rewritten) : [];
  // Pre-derive the user's screenshot data URL once so the JSX (and its click
  // handler) never re-narrow `detail.userScreenshot` inside a closure.
  const userShot = detail?.userScreenshot ?? null;
  const userShotUrl = userShot !== null ? `data:${userShot.mimeType};base64,${userShot.data}` : null;

  return (
    <div className="rv-wrap">
      <header className="rv-topbar">
        <button className="rv-back" type="button" onClick={() => navigate("/")}>
          ◀ 返回对话
        </button>
        <span className="rv-title">标注修正台</span>
        <span className="rv-spacer" />
        {isAdmin && agreement !== null && (
          <span className="rv-agreement-inline">
            <span
              className="rv-help"
              data-tip="一致率：标注员对同一记录的判定（通过/需修正）相同的比例（成对一致率）。越高表示标注越一致、标注规范越清晰。"
              aria-label="一致率说明"
            >
              一致率{" "}
              <b>
                {agreement.agreement.pairwiseAgreementPct === null
                  ? "—"
                  : `${Math.round(agreement.agreement.pairwiseAgreementPct * 100)}%`}
              </b>
            </span>
            {" · "}
            <span
              className="rv-help"
              data-tip="Fleiss κ（卡帕系数）：衡量多位标注员判定的一致程度，并扣除“碰巧一致”的影响，比原始一致率更可信。范围 −1 ~ 1：≥0.8 很好，0.6~0.8 较好，0.2~0.6 一般，≤0 表示比随机猜测还差（样本很少时会偏低甚至为负）。"
              aria-label="Fleiss κ 说明"
            >
              Fleiss κ{" "}
              <b>{agreement.agreement.fleissKappa === null ? "—" : agreement.agreement.fleissKappa.toFixed(2)}</b>
            </span>
          </span>
        )}
      </header>

      {error !== null && <div className="rv-error">加载失败：{error}</div>}

      <div className="rv-tabs">
        {CHIPS.filter((c) => !c.admin || isAdmin).map((c) => {
          const n = countFor(counts, c.key);
          return (
            <button
              key={c.key}
              type="button"
              className={`rv-tab${active === c.key ? " on" : ""}`}
              onClick={() => setActive(c.key)}
            >
              {c.label}
              {n !== undefined && <span className="rv-tab-n">{n}</span>}
            </button>
          );
        })}
      </div>

      {isSeeds ? (
        <ul className="rv-list">
          {seeds.length === 0 && <li className="rv-empty">还没有已存种子</li>}
          {seeds.map((s, i) => (
            <li key={`${s.responseId ?? "x"}-${i}`} className="rv-row">
              <div className="rv-q">{s.question || "(无问题)"}</div>
              <div className="rv-seed-after">{s.after}</div>
              <div className="rv-meta">
                {s.reasons.map((r) => (
                  <span key={r} className="rv-chip">
                    {r}
                  </span>
                ))}
                {s.tenant !== null && <span className="rv-tenant">{s.tenant}</span>}
                <span className="rv-status">{s.annotator}</span>
                <span className="rv-status">{new Date(s.at).toLocaleString()}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rv-panes">
          <ul className="rv-list">
            {items.length === 0 && <li className="rv-empty">该筛选下暂无记录</li>}
            {items.map((it) => (
              <li
                key={it.annotationId}
                className={`rv-row${selected === it.annotationId ? " sel" : ""}`}
                onClick={() => void openItem(it.annotationId)}
              >
                <div className="rv-q">{it.question || "(无问题文本)"}</div>
                <div className="rv-meta">
                  {signalBadge(it.signal, it.reason)}
                  {it.implicit && IMPLICIT_BADGE}
                  {it.tenant !== null && <span className="rv-tenant">{it.tenant}</span>}
                  <span className="rv-status">{STATUS_LABELS[it.status]}</span>
                  {it.hasGold && <span className="rv-gold">已有 gold</span>}
                </div>
              </li>
            ))}
          </ul>

          <div className="rv-detail">
            {detail === null ? (
              <div className="rv-empty">从左侧选择一条进行标注</div>
            ) : (
              <>
                <div className="rv-qq">{detail.question || "(无问题文本)"}</div>
                <div className="rv-sig">
                  信号 {signalBadge(detail.signal)}
                  {detail.implicit && IMPLICIT_BADGE}{" "}
                  · 置信{" "}
                  {detail.confident === null ? "未知" : detail.confident ? "是" : "否"}
                  {detail.tenant !== null && (
                    <>
                      {" · 租户 "}
                      <span className="rv-tenant">{detail.tenant}</span>
                    </>
                  )}
                </div>

                {detail.context.length > 0 && (
                  <details className="rv-context">
                    <summary>对话上下文 · {detail.context.length} 轮</summary>
                    {detail.context.map((t, i) => (
                      <div key={i} className={`rv-ctx-turn ${t.role}`}>
                        <b>{t.role === "user" ? "用户" : "小环"}</b>：
                        {t.role === "user" ? t.text : t.directAnswer}
                      </div>
                    ))}
                  </details>
                )}

                {(detail.userReasons.length > 0 ||
                  detail.userNote !== null ||
                  detail.userScreenshot !== null) && (
                  <section className="rv-userctx">
                    <div className="rv-userctx-h">
                      用户反馈上下文
                      <span className="rv-badge down">点踩</span>
                    </div>
                    {detail.userReasons.length > 0 && (
                      <div className="rv-userctx-reasons">
                        {detail.userReasons.map((r) => (
                          // Read-only echo of what the user picked — not the reviewer's
                          // editable correction tags. A span (not a button) so it cannot
                          // be toggled; it carries the selected look for recognition.
                          <span key={r} className="aa-chip ro" aria-pressed="true">
                            {r}
                            <svg
                              className="tick"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={3}
                              aria-hidden="true"
                            >
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          </span>
                        ))}
                      </div>
                    )}
                    {detail.userNote !== null && detail.userNote.trim().length > 0 && (
                      <blockquote className="rv-userctx-note">{detail.userNote}</blockquote>
                    )}
                    {userShotUrl !== null && (
                      <button
                        type="button"
                        className="rv-userctx-shot"
                        onClick={() => setLightbox(userShotUrl)}
                        title="点击查看大图"
                      >
                        <img src={userShotUrl} alt="用户附带的截图" />
                      </button>
                    )}
                    {detail.sessionId.length > 0 && (
                      <button
                        type="button"
                        className="rv-userctx-link"
                        onClick={() => navigate(`/admin/conversations/${detail.sessionId}`)}
                      >
                        打开完整对话
                      </button>
                    )}
                  </section>
                )}

                <div className="rv-pane-h">模型答案 · 只读 (before)</div>
                <pre className="rv-before">{detail.before || "(无答案记录)"}</pre>

                {draftLoading ? (
                  <div className="rv-draft rv-draft-busy">
                    <span className="rv-spinner" aria-hidden="true" />
                    正在生成修正草稿…
                  </div>
                ) : draft !== null ? (
                  <div className="rv-draft">
                    AI 初判：{DRAFT_VERDICT_LABEL[draft.verdict]}
                    <span className="rv-draft-note">（仅供参考，不计入一致率）</span>
                  </div>
                ) : null}

                {/* Multi-annotated: every annotation as reference, right above the answer box.
                    Click a chip to load that version into the single editor below. */}
                {currentMulti !== null && (
                  <div className="rv-multi-ref">
                    <div className="rv-pane-h">
                      多标注复核 · {currentMulti.annotations.length} 人 · 最终：
                      {currentMulti.canonicalGold === null ? (
                        <span className="rv-tag">未定</span>
                      ) : (
                        <span className="rv-tag">{currentMulti.decidedBy === "auto" ? "自动" : `${currentMulti.decidedBy} 裁定`}</span>
                      )}
                    </div>
                    {currentMulti.annotations.map((a, i) => (
                      <button
                        key={i}
                        type="button"
                        className="rv-chip"
                        title={a.gold ? `采纳 ${a.annotator} 的答案到下方编辑框` : `${a.annotator}: ${a.verdict}`}
                        disabled={!a.gold}
                        onClick={() => a.gold && setAfter(a.gold)}
                      >
                        <span className={`rv-ann-verdict ${a.verdict}`}>{a.annotator}</span>
                        {" · "}
                        {a.verdict === "pass" ? "通过" : a.verdict === "needs_fix" ? "需修正" : "跳过"}
                        {a.gold ? `：${a.gold.slice(0, 36)}` : ""}
                      </button>
                    ))}
                  </div>
                )}

                <div className="rv-pane-h">
                  {currentMulti !== null ? "最终答案 · 可编辑 → 存为 gold 种子" : "修正答案 · 可编辑 (after) → 存为 gold 种子"}
                </div>

                {showAfterGuide ? (
                  // Empty box: explain the GOLD-seed purpose right where the reviewer
                  // is looking, and offer 起草 (only when a model answer exists).
                  <div className="rv-after-empty">
                    <div className="rv-after-hint">
                      这里写 <b>标准答案（GOLD 种子）</b>，会用来训练和评测，定稿以你为准。
                    </div>
                    {aiMode === "draft" && (
                      <button
                        type="button"
                        className="rv-ai-btn"
                        disabled={rewriting}
                        title="依据模型原回答起一份草稿，你再改"
                        onClick={() => void doDraft()}
                      >
                        {rewriting ? (
                          <>
                            <span className="rv-spinner" aria-hidden="true" /> AI 生成中…
                          </>
                        ) : (
                          "✦ 让 AI 起草，你再改"
                        )}
                      </button>
                    )}
                    <button type="button" className="rv-after-write" onClick={() => setEditingAfter(true)}>
                      {aiMode === "draft" ? "或直接开始写" : "开始写标准答案"}
                    </button>
                  </div>
                ) : (
                  // Has text (or reviewer chose to type): the AI entry becomes a
                  // low-key corner "润色" — a different place and form from 起草.
                  <div className="rv-after-shell">
                    {aiMode === "polish" && (
                      <div className="rv-after-tools">
                        <button
                          type="button"
                          className="rv-ai-btn ghost"
                          disabled={rewriting}
                          title="让 AI 把你的草稿润色成更清晰的标准答案，结果先预览再决定"
                          onClick={() => void doPolish()}
                        >
                          {rewriting ? (
                            <>
                              <span className="rv-spinner" aria-hidden="true" /> 润色中…
                            </>
                          ) : (
                            "✦ AI 润色"
                          )}
                        </button>
                      </div>
                    )}
                    <textarea
                      className="rv-after"
                      value={after}
                      onChange={(e) => {
                        setAfter(e.target.value);
                        setEditingAfter(true);
                        if (polishUndo !== null) setPolishUndo(null);
                      }}
                      placeholder="写出依据原始文档的标准答案…"
                    />
                    {polishUndo !== null && (
                      <div className="rv-undo">
                        已用 AI 润色替换
                        <button type="button" onClick={undoPolish}>
                          撤销
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="rv-pane-h">标注原因</div>
                <div>
                  {REASON_OPTIONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={`rv-chip${reasons.includes(r) ? " on" : ""}`}
                      onClick={() => toggleReason(r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>

                <div className="rv-actions">
                  {currentMulti !== null ? (
                    // Admin finalizing a multi-annotated record: one merged save.
                    <>
                      <button
                        className="rv-btn primary"
                        type="button"
                        disabled={busy || after.trim().length === 0}
                        onClick={() => void saveCanonical(currentMulti.responseId, after)}
                      >
                        保存为最终答案
                      </button>
                      <button className="rv-btn ghost" type="button" disabled={busy} onClick={() => void act("skip")}>
                        跳过
                      </button>
                    </>
                  ) : (
                    VERDICT_ACTIONS.map((v) => (
                      <button
                        key={v.action}
                        className={`rv-btn ${v.variant}`}
                        type="button"
                        disabled={busy || (v.requiresAfter && after.trim().length === 0)}
                        onClick={() => void act(v.action)}
                      >
                        {v.label}
                      </button>
                    ))
                  )}
                </div>

                {polishPreview !== null && (
                  <div className="rv-modal-backdrop" role="dialog" aria-modal="true" aria-label="AI 润色预览">
                    <div className="rv-modal">
                      <div className="rv-modal-h">
                        <span>AI 润色预览</span>
                        <span className="rv-modal-sub">事实不变，只调表述和结构</span>
                      </div>
                      <div className="rv-diff">
                        <div className="rv-diff-col left">
                          <div className="rv-diff-lbl">你的原文</div>
                          <div className="rv-diff-body">{renderDiffSide(diffOps, "left")}</div>
                        </div>
                        <div className="rv-diff-col">
                          <div className="rv-diff-lbl">AI 润色版</div>
                          <div className="rv-diff-body">{renderDiffSide(diffOps, "right")}</div>
                        </div>
                      </div>
                      <div className="rv-modal-f">
                        <span className="rv-modal-note">采纳后还能接着改</span>
                        <button className="rv-btn ghost" type="button" onClick={() => setPolishPreview(null)}>
                          放弃
                        </button>
                        <button className="rv-btn primary" type="button" onClick={adoptPolish}>
                          采纳替换
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {lightbox !== null && (
                  <div
                    className="rv-modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-label="截图大图"
                    onClick={() => setLightbox(null)}
                  >
                    <img className="rv-lightbox-img" src={lightbox} alt="用户附带的截图（大图）" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
